import { BellOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useMemo } from 'react';

import { Tooltip } from 'components/ui';
import {
  type AtlasGqlTask,
  AtlasGqlDispatchMode,
  useSubscribeToNotificationsForWorkContainerMutation,
  useUnsubscribeFromNotificationsForWorkContainerMutation,
  useWorkContainerForSubscriptionButtonQuery,
  useWorkContainerNotificationSubscriptionSettingsQuery,
} from 'types/atlas-graphql';

import { StyledButton } from './NotificationSubscriptions.style';
import {
  getParentCampaignLink,
  getParentWorkOrderLink,
  isParentCampaignSubscribed,
  isParentWorkOrderSubscribed,
} from './utils';

type Props = {
  containerId: string;
  afterSubscribe?: () => void;
};

export const NotificationSubscriptionButton: React.FC<Props> = ({
  containerId,
  afterSubscribe,
}) => {
  const { data } = useWorkContainerForSubscriptionButtonQuery({
    variables: { id: containerId },
    skip: !containerId,
  });
  const workContainer = data?.task;
  const workContainerTypeName = workContainer?.type?.name ?? 'Work Container';
  const workContainerNotificationCopy =
    'You will receive Notifications for new Comments, change in Status, ' +
    (workContainerTypeName === 'Campaign'
      ? 'new Attachments, and Work Orders added to this Campaign'
      : workContainerTypeName === 'Work Order'
        ? 'new Attachments, and Tasks added to this Work Order'
        : 'and new Attachments');

  // TODO: to support in-app notifications, duplicate this query with AtlasGqlDispatchMode.InApp and treat with an "or"
  const { data: workContainerEmailSubscriptionSettingsData } =
    useWorkContainerNotificationSubscriptionSettingsQuery({
      variables: { input: { dispatchMode: AtlasGqlDispatchMode.Email } },
    });
  const notificationsEnabled =
    !!workContainerEmailSubscriptionSettingsData?.workContainerNotificationSubscriptionSettings
      .enabled;

  const [subscribe, { loading: subscribeLoading }] =
    useSubscribeToNotificationsForWorkContainerMutation({
      variables: { input: { containerId } },
      update: (cache, { data }) => {
        if (workContainer && data?.subscribeToNotificationsForWorkContainer?.length) {
          cache.modify({
            id: cache.identify(workContainer),
            fields: {
              subscribedToNotifications: () => true,
            },
          });
        }
      },
      onCompleted: () => {
        if (!!afterSubscribe) {
          afterSubscribe();
        }
      },
    });
  const [unsubscribe, { loading: unsubscribeLoading }] =
    useUnsubscribeFromNotificationsForWorkContainerMutation({
      variables: { input: { containerId } },
      update: (cache, { data }) => {
        if (workContainer && data?.unsubscribeFromNotificationsForWorkContainer) {
          cache.modify({
            id: cache.identify(workContainer),
            fields: {
              subscribedToNotifications: () => false,
            },
          });
        }
      },
    });

  const { parentCampaignSubscribed, parentWorkOrderSubscribed, subscribedTooltipCopy } =
    useMemo(() => {
      if (!!workContainer) {
        const parentCampaignSubscribed = isParentCampaignSubscribed(workContainer as AtlasGqlTask);
        const parentWorkOrderSubscribed = isParentWorkOrderSubscribed(
          workContainer as AtlasGqlTask
        );

        const subscribedTooltipCopy =
          parentCampaignSubscribed || parentWorkOrderSubscribed ? (
            <>
              You are subscribed to Notifications for the{' '}
              {parentCampaignSubscribed ? 'Campaign' : 'Work Order'} this {workContainerTypeName} is
              in. {workContainerNotificationCopy}. Go to the{' '}
              {parentCampaignSubscribed
                ? getParentCampaignLink(workContainer as AtlasGqlTask, false)
                : getParentWorkOrderLink(workContainer as AtlasGqlTask, false)}{' '}
              to Unsubscribe.
            </>
          ) : (
            <>
              You are subscribed to Notifications for this {workContainerTypeName}.{' '}
              {workContainerNotificationCopy}. Click to Unsubscribe.
            </>
          );

        return {
          parentCampaignSubscribed,
          parentWorkOrderSubscribed,
          subscribedTooltipCopy,
        };
      }

      return {
        parentCampaignSubscribed: false,
        parentWorkOrderSubscribed: false,
        subscribedTooltipCopy: <></>,
      };
    }, [workContainer, workContainerTypeName, workContainerNotificationCopy]);

  if (
    workContainer?.subscribedToNotifications ||
    parentCampaignSubscribed ||
    parentWorkOrderSubscribed
  ) {
    return (
      <Tooltip
        title={
          notificationsEnabled ? (
            subscribedTooltipCopy
          ) : (
            <>
              You're subscribed to Notifications for this {workContainerTypeName}, but have
              Notifications disabled. You can re-enable Notifications in{' '}
              <a href="/settings/notification-settings" target="_blank">
                settings
              </a>
              .
            </>
          )
        }
      >
        <span>
          <StyledButton
            data-testid="work-order-unsubscribe"
            onClick={unsubscribe}
            type="primary"
            icon={<CheckCircleOutlined />}
            loading={unsubscribeLoading}
            disabled={
              !notificationsEnabled || parentCampaignSubscribed || parentWorkOrderSubscribed
            }
          >
            Subscribed
          </StyledButton>
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={
        notificationsEnabled ? (
          <>
            Click to subscribe to Notifications for this {workContainerTypeName}.{' '}
            {workContainerNotificationCopy}.
          </>
        ) : (
          <>
            To subscribe to Notifications, enable Work Container Notifications in{' '}
            <a href="/settings/notification-settings" target="_blank">
              settings
            </a>
            .
          </>
        )
      }
    >
      <span>
        <StyledButton
          data-testid="accept-subscription-button"
          style={{ width: '100%' }}
          onClick={subscribe}
          icon={<BellOutlined />}
          loading={subscribeLoading}
          disabled={!notificationsEnabled}
        >
          Subscribe
        </StyledButton>
      </span>
    </Tooltip>
  );
};
